import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import {
  Loader2,
  XCircle,
  Users,
  Calendar,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import { format } from "date-fns";
import PostJoinWelcome from "@/components/challenges/PostJoinWelcome";
import InviteTeamMemberDialog from "@/components/challenges/InviteTeamMemberDialog";

interface InvitationDetails {
  id: string;
  team_id: string | null;
  challenge_id: string;
  invitee_name: string;
  invitee_email: string;
  status: string;
  expires_at: string;
  invitation_type: "join_existing" | "create_team";
  original_inviter_id: string | null;
  inviter_joins_team: boolean;
  challenge_teams: {
    name: string;
  } | null;
  challenges: {
    title: string;
    event_id: string | null;
    events?: {
      id: string;
      name: string;
      slug: string;
    } | null;
  } | null;
}

interface JoinedTeamInfo {
  id: string;
  name: string;
  challengeId: string;
  challengeTitle: string;
  isNew: boolean;
  // Event info for registration CTA
  eventId?: string | null;
  eventName?: string;
  eventSlug?: string;
}

const AcceptTeamInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  
  // Team creation state (for create_team invitations)
  const [showTeamCreation, setShowTeamCreation] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [teamDescription, setTeamDescription] = useState("");
  
  // Post-join welcome state
  const [showWelcome, setShowWelcome] = useState(false);
  const [joinedTeam, setJoinedTeam] = useState<JoinedTeamInfo | null>(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [isRegisteredForEvent, setIsRegisteredForEvent] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getUser();
      setUser(data.user);
    };
    checkAuth();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_, session) => {
        setUser(session?.user ?? null);
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token) {
        setError("Invalid invitation link");
        setLoading(false);
        return;
      }

      try {
        // Create the query promise
        const queryPromise = supabase
          .from("team_member_invitations")
          .select(
            `
            id,
            team_id,
            challenge_id,
            invitee_name,
            invitee_email,
            status,
            expires_at,
            invitation_type,
            original_inviter_id,
            inviter_joins_team,
            challenge_teams(name),
            challenges(title, event_id, events(id, name, slug))
          `
          )
          .eq("token", token)
          .single();

        // Race against 15-second timeout to prevent infinite loading
        const timeoutPromise = new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error("Request timed out")), 15000)
        );

        const { data, error: fetchError } = await Promise.race([
          queryPromise,
          timeoutPromise
        ]);

        if (fetchError) throw fetchError;

        if (!data) {
          setError("Invitation not found");
          return;
        }

        // Check if expired
        if (new Date(data.expires_at) < new Date()) {
          setError("This invitation has expired");
          return;
        }

        // Check if already processed
        if (data.status === "accepted") {
          setError("This invitation has already been accepted");
          return;
        }

        if (data.status === "declined") {
          setError("This invitation was declined");
          return;
        }

        if (data.status !== "pending") {
          setError("This invitation is no longer valid");
          return;
        }

        setInvitation(data as unknown as InvitationDetails);
      } catch (err: any) {
        console.error("Failed to fetch invitation:", err);
        if (err.message === "Request timed out") {
          setError("Unable to load invitation. Please try again or contact support.");
        } else {
          setError("Failed to load invitation details");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  const handleAcceptJoinExisting = async () => {
    if (!invitation || !user || !invitation.team_id) return;

    setProcessing(true);

    try {
      // Add user to the team
      const { error: memberError } = await supabase
        .from("challenge_team_members")
        .insert({
          team_id: invitation.team_id,
          challenge_id: invitation.challenge_id,
          user_id: user.id,
          role: "member",
        });

      if (memberError) {
        if (memberError.code === "23505") {
          toast.info("You're already a member of this team!");
        } else {
          throw memberError;
        }
      }

      // Update invitation status
      await supabase
        .from("team_member_invitations")
        .update({ status: "accepted" })
        .eq("id", invitation.id);

      // Notify inviter that invitation was accepted
      try {
        await supabase.functions.invoke("send-invite-accepted-notification", {
          body: {
            invitation_id: invitation.id,
            context_type: "team",
          },
        });
      } catch (notifyErr) {
        console.error("Failed to send acceptance notification:", notifyErr);
        // Don't fail the whole flow for notification errors
      }

      // Check if user is registered for the event
      let userIsRegistered = true;
      if (invitation.challenges?.event_id && user) {
        const { data: eventReg } = await supabase
          .from("event_registrations")
          .select("id")
          .eq("user_id", user.id)
          .eq("event_id", invitation.challenges.event_id)
          .maybeSingle();
        userIsRegistered = !!eventReg;
      }
      setIsRegisteredForEvent(userIsRegistered);

      // Show welcome modal
      setJoinedTeam({
        id: invitation.team_id,
        name: invitation.challenge_teams?.name || "Your Team",
        challengeId: invitation.challenge_id,
        challengeTitle: invitation.challenges?.title || "the challenge",
        isNew: false,
        eventId: invitation.challenges?.event_id,
        eventName: invitation.challenges?.events?.name,
        eventSlug: invitation.challenges?.events?.slug,
      });
      setShowWelcome(true);
      toast.success("You've joined the team!");
    } catch (err: any) {
      console.error("Failed to accept invitation:", err);
      toast.error(err.message || "Failed to join team");
    } finally {
      setProcessing(false);
    }
  };

  const handleAcceptCreateTeam = async () => {
    // For create_team invitations, show the team creation form
    setShowTeamCreation(true);
  };

  const handleCreateTeam = async () => {
    if (!invitation || !user || !teamName.trim()) {
      toast.error("Please enter a team name");
      return;
    }

    setProcessing(true);

    try {
      // Create the new team
      const { data: newTeam, error: teamError } = await supabase
        .from("challenge_teams")
        .insert({
          challenge_id: invitation.challenge_id,
          name: teamName.trim(),
          description: teamDescription.trim() || null,
          created_by: user.id,
        })
        .select()
        .single();

      if (teamError) throw teamError;

      // Add invitee as team lead
      const { error: leadError } = await supabase
        .from("challenge_team_members")
        .insert({
          team_id: newTeam.id,
          challenge_id: invitation.challenge_id,
          user_id: user.id,
          role: "lead",
        });

      if (leadError) throw leadError;

      // If original inviter wants to join, add them as member
      if (invitation.inviter_joins_team && invitation.original_inviter_id) {
        const { error: inviterError } = await supabase
          .from("challenge_team_members")
          .insert({
            team_id: newTeam.id,
            challenge_id: invitation.challenge_id,
            user_id: invitation.original_inviter_id,
            role: "member",
          });

        if (inviterError) {
          console.error("Failed to add original inviter:", inviterError);
          // Don't fail the whole flow for this
        }
      }

      // Update invitation status
      await supabase
        .from("team_member_invitations")
        .update({ 
          status: "accepted",
          team_id: newTeam.id, // Now we can set the team_id
        })
        .eq("id", invitation.id);

      // Notify inviter that invitation was accepted (and they were added to team if applicable)
      try {
        await supabase.functions.invoke("send-invite-accepted-notification", {
          body: {
            invitation_id: invitation.id,
            context_type: "team",
            new_team_id: newTeam.id,
          },
        });
      } catch (notifyErr) {
        console.error("Failed to send acceptance notification:", notifyErr);
        // Don't fail the whole flow for notification errors
      }

      // Check if user is registered for the event
      let userIsRegistered = true;
      if (invitation.challenges?.event_id && user) {
        const { data: eventReg } = await supabase
          .from("event_registrations")
          .select("id")
          .eq("user_id", user.id)
          .eq("event_id", invitation.challenges.event_id)
          .maybeSingle();
        userIsRegistered = !!eventReg;
      }
      setIsRegisteredForEvent(userIsRegistered);

      // Show welcome modal
      setJoinedTeam({
        id: newTeam.id,
        name: newTeam.name,
        challengeId: invitation.challenge_id,
        challengeTitle: invitation.challenges?.title || "the challenge",
        isNew: true,
        eventId: invitation.challenges?.event_id,
        eventName: invitation.challenges?.events?.name,
        eventSlug: invitation.challenges?.events?.slug,
      });
      setShowWelcome(true);
      toast.success("Team created! You're now the team lead.");
    } catch (err: any) {
      console.error("Failed to create team:", err);
      toast.error(err.message || "Failed to create team");
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!invitation) return;

    setProcessing(true);

    try {
      await supabase
        .from("team_member_invitations")
        .update({ status: "declined" })
        .eq("id", invitation.id);

      toast.success("Invitation declined");
      navigate("/");
    } catch (err: any) {
      console.error("Failed to decline invitation:", err);
      toast.error(err.message || "Failed to decline invitation");
    } finally {
      setProcessing(false);
    }
  };

  const handleWelcomeClose = () => {
    setShowWelcome(false);
    navigate("/dashboard/challenges");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button asChild>
              <Link to="/">Go to Homepage</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show welcome modal if team was joined/created
  if (showWelcome && joinedTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <PostJoinWelcome
          open={showWelcome}
          onOpenChange={handleWelcomeClose}
          teamName={joinedTeam.name}
          teamId={joinedTeam.id}
          challengeId={joinedTeam.challengeId}
          challengeTitle={joinedTeam.challengeTitle}
          isNewTeam={joinedTeam.isNew}
          onInviteClick={() => {
            setShowWelcome(false);
            setShowInviteDialog(true);
          }}
          eventId={joinedTeam.eventId}
          eventName={joinedTeam.eventName}
          eventSlug={joinedTeam.eventSlug}
          isRegisteredForEvent={isRegisteredForEvent}
        />
        {showInviteDialog && (
          <InviteTeamMemberDialog
            open={showInviteDialog}
            onOpenChange={setShowInviteDialog}
            teamId={joinedTeam.id}
            teamName={joinedTeam.name}
            challengeId={joinedTeam.challengeId}
            isTeamLead={joinedTeam.isNew}
            onInviteSent={() => {
              setShowInviteDialog(false);
              navigate("/dashboard/challenges");
            }}
          />
        )}
      </div>
    );
  }

  if (!user) {
    const isCreateTeam = invitation?.invitation_type === "create_team";
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            {isCreateTeam ? (
              <Sparkles className="h-12 w-12 text-primary mx-auto mb-2" />
            ) : (
              <Users className="h-12 w-12 text-primary mx-auto mb-2" />
            )}
            <CardTitle>
              {isCreateTeam ? "Lead a New Team!" : "Team Invitation"}
            </CardTitle>
            <CardDescription>
              {isCreateTeam ? (
                <>
                  You've been invited to create and lead a new team for the
                  challenge: <strong>{invitation?.challenges?.title || "the challenge"}</strong>
                </>
              ) : (
                <>
                  You've been invited to join{" "}
                  <strong>{invitation?.challenge_teams?.name}</strong> for the
                  challenge: <strong>{invitation?.challenges?.title || "the challenge"}</strong>
                </>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg space-y-2">
              {!isCreateTeam && invitation?.challenge_teams && (
                <div className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>Team: {invitation.challenge_teams.name}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>
                  Expires:{" "}
                  {invitation?.expires_at &&
                    format(new Date(invitation.expires_at), "MMM d, yyyy")}
                </span>
              </div>
            </div>

            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 p-4 rounded-lg">
              <div className="flex gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    Sign in required
                  </p>
                  <p className="text-amber-700 dark:text-amber-300">
                    Please sign in or create an account to accept this
                    invitation.
                  </p>
                </div>
              </div>
            </div>

            <Button asChild className="w-full">
              <Link to={`/login?redirect=${encodeURIComponent(`/invite/accept/${token}`)}`}>
                Sign In to Accept
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isCreateTeam = invitation?.invitation_type === "create_team";

  // Team creation form for create_team invitations
  if (isCreateTeam && showTeamCreation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <Sparkles className="h-12 w-12 text-primary mx-auto mb-2" />
            <CardTitle>Create Your Team</CardTitle>
            <CardDescription>
              Name your team for "{invitation?.challenges?.title || "the challenge"}"
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="teamName">Team Name *</Label>
              <Input
                id="teamName"
                placeholder="e.g., The Innovators"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="teamDescription">Description (optional)</Label>
              <Textarea
                id="teamDescription"
                placeholder="What's your team's focus or approach?"
                value={teamDescription}
                onChange={(e) => setTeamDescription(e.target.value)}
                rows={3}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowTeamCreation(false)}
                disabled={processing}
              >
                Back
              </Button>
              <Button
                className="flex-1"
                onClick={handleCreateTeam}
                disabled={processing || !teamName.trim()}
              >
                {processing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Create Team"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          {isCreateTeam ? (
            <Sparkles className="h-12 w-12 text-primary mx-auto mb-2" />
          ) : (
            <Users className="h-12 w-12 text-primary mx-auto mb-2" />
          )}
          <CardTitle>
            {isCreateTeam ? "Lead a New Team!" : "Team Invitation"}
          </CardTitle>
          <CardDescription>
            {isCreateTeam ? (
              <>
                You've been invited to create and lead a new team for this challenge
              </>
            ) : (
              <>
                You've been invited to join{" "}
                <strong>{invitation?.challenge_teams?.name}</strong>
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 p-4 rounded-lg space-y-3">
            <div>
              <p className="text-sm text-muted-foreground">Challenge</p>
              <p className="font-medium">{invitation?.challenges?.title || "the challenge"}</p>
            </div>
            {!isCreateTeam && invitation?.challenge_teams && (
              <div>
                <p className="text-sm text-muted-foreground">Team</p>
                <p className="font-medium">{invitation.challenge_teams.name}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-muted-foreground">Invitation for</p>
              <p className="font-medium">{invitation?.invitee_name}</p>
              <p className="text-sm text-muted-foreground">
                {invitation?.invitee_email}
              </p>
            </div>
          </div>

          {isCreateTeam && (
            <div className="bg-primary/5 border border-primary/20 p-4 rounded-lg">
              <p className="text-sm">
                When you accept, you'll name your team and become its leader. 
                You can then invite others to join.
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleDecline}
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Decline"
              )}
            </Button>
            <Button
              className="flex-1"
              onClick={isCreateTeam ? handleAcceptCreateTeam : handleAcceptJoinExisting}
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isCreateTeam ? (
                "Accept & Create Team"
              ) : (
                "Accept & Join"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptTeamInvite;
